import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UsersService } from '../users-service/users.service';
import { CommonModule, NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { GrpcStatusEvent } from '@ngx-grpc/common';
import { AuthService } from '../../../authentication/service/auth.service';
import { Router, RouterLink } from '@angular/router';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { MatIcon } from '@angular/material/icon';
import {
    CreateGroupRequest,
    UserGroupDto,
    UserRoleDto
} from '../../../protos/generated/admin.pb';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-add-user-group',
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        MatInputModule,
        MatCheckbox,
        MatButtonModule,
        MatTreeModule,
        MatIcon,
        RouterLink,
        TranslatePipe
    ],
    templateUrl: './add-user-group.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./add-user-group.component.scss']
})
export class AddUserGroupComponent implements OnInit {
    userGroups: UserGroupDto[] = [];
    roleMap: Map<string, RoleTree> = new Map<string, RoleTree>();
    addUserGroupForm!: FormGroup;
    selectedRoles: number[] = [];
    errorMessage: string = '';
    SucessMessage: boolean = false;
    isLoading: boolean = false;
    public dataSource = new MatTreeNestedDataSource<RoleTree>();
    childrenAccessor = (node: RoleTree) => node.children ?? [];

    constructor(
        private fb: FormBuilder,
        private userService: UsersService,
        private authService: AuthService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.addUserGroupForm = this.fb.group({
            title: ['', Validators.required]
        });
        this.FetchRoles();
    }

    FetchRoles() {
        this.userService.getAllRoles().subscribe({
            next: response => {
                this.convertRolesToUserGroupsTree(response.userRoles ?? []);
            }
        });
    }

    convertRolesToUserGroupsTree(roles: UserRoleDto[]) {
        roles.forEach(role => {
            this.roleMap.set(role.title, new RoleTree(role.id, role.title));
        });

        roles.forEach(role => {
            if (role.title.split('.').length === 3) {
                // child
                const parentTitle = role.title
                    .split('.')
                    .slice(0, 2)
                    .join('.');
                const parentRole = this.roleMap.get(parentTitle);
                if (parentRole) {
                    parentRole.children.push(this.roleMap.get(role.title)!);
                    this.roleMap.delete(role.title);
                }
            }
        });

        this.dataSource.data = Array.from(this.roleMap.values());
    }

    onCreateUserGroup() {
        this.isLoading = true;
        const createGroupRequest = new CreateGroupRequest();
        createGroupRequest.title = this.addUserGroupForm.get('title')?.value;
        createGroupRequest.roles = this.selectedRoles;

        this.userService.addNewUserGroup(createGroupRequest).subscribe({
            next: () => {
                this.SucessMessage = true;
                setTimeout(() => {
                    this.router.navigate(['/users/user-groups']);
                }, 2000);
            },
            error: (error: GrpcStatusEvent) => {
                if (error.statusCode === 16) {
                    this.authService.logOut();
                } else {
                    this.errorMessage = error.statusMessage;
                }
                this.isLoading = false;
            },
            complete: () => {
                this.isLoading = false;
            }
        });
    }
    closeAlert() {
        this.errorMessage = '';
        this.SucessMessage = false;
    }

    toggleRoleSelection(roleId: number, checked: boolean) {
        if (checked) {
            this.selectedRoles.push(roleId);
        } else {
            this.selectedRoles = this.selectedRoles.filter(id => id !== roleId);
        }
    }

    areAllChildrenChecked(role: RoleTree): boolean {
        if (!role.children || role.children.length === 0) {
            return false;
        }
        return role.children.every(child =>
            this.selectedRoles.includes(child.id)
        );
    }

    areSomeChildrenRoleChecked(role: RoleTree): boolean {
        if (!role.children || role.children.length === 0) {
            return false;
        }
        return role.children.some(child =>
            this.selectedRoles.includes(child.id)
        );
    }

    toggleAllRoles(role: RoleTree, checked: boolean) {
        if (checked) {
            for (let childRole of role.children) {
                if (!this.selectedRoles.includes(childRole.id)) {
                    this.selectedRoles.push(childRole.id);
                }
            }
        } else {
            for (let childRole of role.children) {
                this.selectedRoles = this.selectedRoles.filter(
                    id => id !== childRole.id
                );
            }
        }
    }
    hasChild = (_: number, node: RoleTree) =>
        !!node.children && node.children.length > 0;
}

export class RoleTree {
    id: number;
    title: string;
    children: RoleTree[];

    constructor(id: number, title: string, children: RoleTree[] = []) {
        this.id = id;
        this.title = title;
        this.children = children;
    }
}
